import type { DocumentationFile } from '@si/types';
import { patch } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './queryKeys';

interface Params {
  projectId: string;
  documentationFileId: number;
  comment: string;
}

export function useUpdateDocumentationFileComment() {
  const queryClient = useQueryClient();

  const { mutate, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: ({ projectId, documentationFileId, comment }: Params) =>
      patch({
        url: `/projects/${projectId}/documentation-files/${documentationFileId}`,
        body: { comment },
      }),
    onSuccess: (_, { projectId, documentationFileId, comment }) => {
      const queryKey = queryKeys.getDocumentationFiles(projectId);
      const cache = queryClient.getQueryData<{
        documentationFiles: DocumentationFile[];
      }>(queryKey);

      if (cache) {
        queryClient.setQueryData(queryKey, {
          documentationFiles: cache.documentationFiles.map((file) =>
            file.documentationFileId === documentationFileId ? { ...file, comment } : file
          ),
        });
      }
    },
  });

  const updateComment = useCallback((params: Params) => mutate(params), [mutate]);

  return {
    updateComment,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
